import type { ApiErrorBody, ErrorCode } from '@koyomi/shared';
import { useAuthStore } from '../app/auth-store.ts';

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
const BASE = `${API_URL}/api/v1`;

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: ErrorCode, message: string, public readonly details?: unknown) {
    super(message);
  }
}

interface Options {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Não anexa X-Business-Id. */
  global?: boolean;
  signal?: AbortSignal;
  retry?: boolean;
  /** Recebe o `meta` da resposta, quando houver. */
  onMeta?: (meta: unknown) => void;
}

let refreshing: Promise<boolean> | null = null;

/** Renova o access token via cookie httpOnly; uma tentativa por vez. */
export async function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' });
        if (!res.ok) return false;
        const json = await res.json();
        useAuthStore.getState().setSession(json.data);
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

/** Variante que devolve também o `meta` da resposta (paginação). */
export async function apiFull<T, M = unknown>(path: string, opts: Options = {}): Promise<{ data: T; meta: M | null }> {
  const holder: { meta: unknown } = { meta: null };
  const data = await api<T>(path, { ...opts, onMeta: (m) => { holder.meta = m; } });
  return { data, meta: (holder.meta as M) ?? null };
}

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const { accessToken, businessId } = useAuthStore.getState();
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (opts.query) for (const [k, v] of Object.entries(opts.query)) if (v !== undefined) url.searchParams.set(k, String(v));
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (!opts.global && businessId) headers['x-business-id'] = businessId;

  const res = await fetch(url, { method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'), headers, body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined, credentials: 'include', signal: opts.signal });
  if (res.status === 204) return undefined as T;
  const json = (await res.json().catch(() => null)) as { data?: T; meta?: unknown } | ApiErrorBody | null;
  if (!res.ok) {
    const err = (json as ApiErrorBody | null)?.error;
    if (res.status === 401 && err?.code !== 'INVALID_CREDENTIALS' && opts.retry !== false && accessToken) {
      const ok = await refreshSession();
      if (ok) return api<T>(path, { ...opts, retry: false });
      useAuthStore.getState().clear();
    }
    throw new ApiError(res.status, err?.code ?? 'INTERNAL', err?.message ?? res.statusText, err?.details);
  }
  if (opts.onMeta && json && 'meta' in json) opts.onMeta((json as { meta?: unknown }).meta ?? null);
  return (json as { data: T }).data;
}

/** Download binário (CSV etc.) pelo mesmo caminho do `api`: token, X-Business-Id, refresh em 401 e erros JSON. */
export async function apiBlob(path: string, opts: Omit<Options, 'body' | 'method' | 'onMeta'> = {}): Promise<Blob> {
  const { accessToken, businessId } = useAuthStore.getState();
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (opts.query) for (const [k, v] of Object.entries(opts.query)) if (v !== undefined) url.searchParams.set(k, String(v));
  const headers: Record<string, string> = {};
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (!opts.global && businessId) headers['x-business-id'] = businessId;
  const res = await fetch(url, { method: 'GET', headers, credentials: 'include', signal: opts.signal });
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as ApiErrorBody | null;
    const err = json?.error;
    if (res.status === 401 && err?.code !== 'INVALID_CREDENTIALS' && opts.retry !== false && accessToken) {
      const ok = await refreshSession();
      if (ok) return apiBlob(path, { ...opts, retry: false });
      useAuthStore.getState().clear();
    }
    throw new ApiError(res.status, err?.code ?? 'INTERNAL', err?.message ?? res.statusText, err?.details);
  }
  return res.blob();
}

/** Baixa um Blob como arquivo e libera a URL depois que o navegador iniciou o download. */
export function saveBlob(blob: Blob, filename: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}
